// Document ⇄ ag-psd の写像 (純ロジック寄り。表面の読み書きは引数で受ける)。
//
// 損失は ConversionReport に集める (docs/psd-compat.md に一覧化する)。
// - 調整レイヤー / テキスト / シェイプ / スマートオブジェクトは Phase 3 で対応。それまでは
//   ラスタ画像 (imageData) があればラスターレイヤーとして読み込み、警告を出す。
// - 16bit / CMYK / Lab は ag-psd が RGB 8bit に変換して読むが、書き戻しは RGB 8bit になる。

import type { Layer as PsdLayer, Psd } from 'ag-psd'
import type { Document, Layer, LayerId, BlendMode, LayerMask } from '@/types/document'
import { createEmptyDocument, createRasterLayer, createGroupLayer, createTextLayer, createAdjustmentLayer, createFillLayer, createSmartLayer, newLayerId, newPixelId } from '@/model/defaults'
import { PixelSurface } from '@/pixels/pixelSurface'
import { surfaceFromRgba } from '@/pixels/imageDataUtil'
import type { Rect } from '@/types/geometry'

export interface ConversionWarning {
  layerName: string
  kind: 'adjustment' | 'text' | 'vector' | 'smart' | 'effects' | 'vectorMask' | 'unsupported' | 'other'
  message: string
}

export interface ConversionReport {
  warnings: ConversionWarning[]
}

export { blendModeToPsd, blendModeFromPsd } from './blend'
import { blendModeToPsd, blendModeFromPsd } from './blend'
import { adjustmentToPsd, adjustmentFromPsd, fillToPsd, fillFromPsd } from './adjustments'
import { stylesToPsd, stylesFromPsd } from './effects'

export interface PixelDataLike {
  width: number
  height: number
  data: Uint8Array | Uint8ClampedArray | Uint16Array | Float32Array
}

/** 8/16/32bit の画素配列を 8bit に正規化する */
function toBytes(pd: PixelDataLike): Uint8ClampedArray {
  const d = pd.data
  if (d instanceof Uint8ClampedArray) return d
  if (d instanceof Uint8Array) return new Uint8ClampedArray(d.buffer, d.byteOffset, d.byteLength)
  const out = new Uint8ClampedArray(d.length)
  if (d instanceof Uint16Array) for (let i = 0; i < d.length; i++) out[i] = Math.round(d[i]! / 257)
  else for (let i = 0; i < d.length; i++) out[i] = Math.round(d[i]! * 255)
  return out
}

/** ag-psd の imageData (RGBA) から表面を作る。left/top はレイヤーのオフセットになる */
function surfaceFromPixelData(pd: PixelDataLike): PixelSurface {
  return surfaceFromRgba(newPixelId(), pd.width, pd.height, toBytes(pd))
}

/** RGBA の R チャンネルをグレー 1ch に */
function maskSurfaceFromPixelData(pd: PixelDataLike, rect: Rect): PixelSurface {
  const s = new PixelSurface(newPixelId(), 1)
  const bytes = toBytes(pd)
  const gray = new Uint8ClampedArray(pd.width * pd.height)
  for (let i = 0; i < gray.length; i++) gray[i] = bytes[i * 4]!
  s.writeRect({ x: rect.x, y: rect.y, w: pd.width, h: pd.height }, gray)
  return s
}

export interface ImportedPsd {
  document: Document
  surfaces: PixelSurface[]
  report: ConversionReport
}

/** ag-psd で読んだ Psd を Document へ。imageData を使って読んでいること (useImageData: true) */
export function psdToDocument(psd: Psd, name: string): ImportedPsd {
  const doc = createEmptyDocument({ name, width: psd.width, height: psd.height, background: 'transparent' })
  const surfaces: PixelSurface[] = []
  const report: ConversionReport = { warnings: [] }

  const convert = (pl: PsdLayer): LayerId | null => {
    const name = pl.name ?? 'レイヤー'
    const common = {
      name,
      visible: !pl.hidden,
      opacity: pl.opacity ?? 1,
      fillOpacity: pl.fillOpacity ?? 1,
      clipToBelow: !!pl.clipping,
      lock: { all: false, pixels: !!pl.protected?.composite, position: !!pl.protected?.position, transparency: !!pl.transparencyProtected || !!pl.protected?.transparency }
    }
    let mask: LayerMask | undefined
    if (pl.mask && pl.mask.imageData) {
      const left = pl.mask.left ?? 0, top = pl.mask.top ?? 0
      const ms = maskSurfaceFromPixelData(pl.mask.imageData, { x: 0, y: 0, w: pl.mask.imageData.width, h: pl.mask.imageData.height })
      surfaces.push(ms)
      mask = { pixelId: ms.id, enabled: !pl.mask.disabled, linked: true, density: (pl.mask.userMaskDensity ?? 255) / 255, feather: pl.mask.userMaskFeather ?? 0, offset: { x: left, y: top } }
    }
    const fx = stylesFromPsd(pl.effects)
    for (const sk of fx.skipped) report.warnings.push({ layerName: name, kind: 'effects', message: `レイヤースタイル: ${sk} は対応していません` })
    if (pl.vectorMask) report.warnings.push({ layerName: name, kind: 'vectorMask', message: 'ベクターマスクはラスタ化されません (Phase 3)' })
    const bmNormal = (): BlendMode => {
      const b = blendModeFromPsd(pl.blendMode, 'normal')
      return b === 'pass-through' ? 'normal' : b
    }

    if (pl.children) {
      const g = createGroupLayer({ id: newLayerId(), ...common, blendMode: blendModeFromPsd(pl.blendMode, 'pass-through'), expanded: pl.opened ?? true })
      if (mask) g.mask = mask
      if (fx.styles) g.styles = fx.styles
      for (const c of pl.children) {
        const cid = convert(c)
        if (cid) g.children.push(cid)
      }
      doc.layers[g.id] = g
      return g.id
    }
    if (pl.adjustment) {
      const adj = adjustmentFromPsd(pl.adjustment)
      if (adj) {
        const layer = createAdjustmentLayer({ id: newLayerId(), ...common, blendMode: bmNormal(), adjustment: adj })
        if (mask) layer.mask = mask
        if (fx.styles) layer.styles = fx.styles
        doc.layers[layer.id] = layer
        if (pl.adjustment.type === 'hue/saturation' && [pl.adjustment.reds, pl.adjustment.yellows, pl.adjustment.greens, pl.adjustment.cyans, pl.adjustment.blues, pl.adjustment.magentas].some(c => c && (c.hue || c.saturation || c.lightness))) report.warnings.push({ layerName: name, kind: 'adjustment', message: '色相・彩度の系統色別の設定は読み込みません (マスターのみ)' })
        return layer.id
      }
      report.warnings.push({ layerName: name, kind: 'adjustment', message: `調整レイヤー (${pl.adjustment.type}) は対応していないため読み飛ばします` })
      return null
    }
    if (pl.vectorFill && !pl.vectorStroke && !pl.vectorMask) {
      const fill = fillFromPsd(pl.vectorFill)
      if (fill) {
        const layer = createFillLayer({ id: newLayerId(), ...common, blendMode: bmNormal(), fill })
        if (mask) layer.mask = mask
        if (fx.styles) layer.styles = fx.styles
        doc.layers[layer.id] = layer
        return layer.id
      }
      report.warnings.push({ layerName: name, kind: 'other', message: '塗りつぶしレイヤー (パターン / ノイズ) はラスタ画像として読み込みます' })
    }
    if (pl.text && typeof pl.text.text === 'string') {
      // テキストレイヤーとして読み込む (基本スタイルのみ)
      const st = pl.text.style ?? {}
      const fc = st.fillColor as { r?: number; g?: number; b?: number } | undefined
      const hexOf = (c?: { r?: number; g?: number; b?: number }) => (c ? '#' + [c.r ?? 0, c.g ?? 0, c.b ?? 0].map(v => Math.round(v).toString(16).padStart(2, '0')).join('') : '#000000')
      const tr = pl.text.transform ?? [1, 0, 0, 1, pl.left ?? 0, pl.top ?? 0]
      const bm2 = blendModeFromPsd(pl.blendMode, 'normal')
      const layer = createTextLayer({
        id: newLayerId(),
        ...common,
        blendMode: bm2 === 'pass-through' ? 'normal' : bm2,
        text: pl.text.text,
        position: { x: tr[4] ?? 0, y: tr[5] ?? 0 },
        style: {
          fontFamily: st.font?.name ?? 'sans-serif', fontSize: st.fontSize ?? 48, fontWeight: st.fauxBold ? 700 : 400, italic: !!st.fauxItalic, hex: hexOf(fc),
          lineHeight: 1.2, letterSpacing: st.tracking ? ((st.tracking / 1000) * (st.fontSize ?? 48)) : 0,
          align: pl.text.paragraphStyle?.justification === 'center' ? 'center' : pl.text.paragraphStyle?.justification === 'right' ? 'right' : 'left',
          orientation: pl.text.orientation === 'vertical' ? 'vertical' : 'horizontal'
        }
      })
      if (mask) layer.mask = mask
      if (fx.styles) layer.styles = fx.styles
      doc.layers[layer.id] = layer
      report.warnings.push({ layerName: name, kind: 'text', message: 'テキストは基本スタイルだけを読み込みます (書式の一部は失われます)' })
      return layer.id
    }
    if (pl.placedLayer && pl.imageData && pl.imageData.width > 0 && pl.imageData.height > 0) {
      // 配置画像のラスタを元画像にしたスマートオブジェクトとして読む (変形は焼き込まれた状態)
      const src = surfaceFromPixelData(pl.imageData)
      surfaces.push(src)
      const bm3 = blendModeFromPsd(pl.blendMode, 'normal')
      const layer = createSmartLayer({ id: newLayerId(), ...common, blendMode: bm3 === 'pass-through' ? 'normal' : bm3, sourceId: src.id, sourceRect: { x: 0, y: 0, w: pl.imageData.width, h: pl.imageData.height }, transform: [1, 0, 0, 0, 1, 0, pl.left ?? 0, pl.top ?? 0, 1] })
      if (mask) layer.mask = mask
      if (fx.styles) layer.styles = fx.styles
      doc.layers[layer.id] = layer
      report.warnings.push({ layerName: name, kind: 'smart', message: 'スマートオブジェクトは配置時の画素を元画像として読み込みます (元ファイルは保持しません)' })
      return layer.id
    }
    const bm = blendModeFromPsd(pl.blendMode, 'normal')
    const blendMode: BlendMode = bm === 'pass-through' ? 'normal' : bm
    let surface: PixelSurface
    const offset = { x: pl.left ?? 0, y: pl.top ?? 0 }
    if (pl.imageData && pl.imageData.width > 0 && pl.imageData.height > 0) {
      surface = surfaceFromPixelData(pl.imageData)
    } else {
      surface = new PixelSurface(newPixelId(), 4)
      if (pl.adjustment || pl.text || pl.placedLayer) report.warnings.push({ layerName: name, kind: 'unsupported', message: 'ラスタ画像が無いため空のレイヤーになります' })
    }
    surfaces.push(surface)
    const layer = createRasterLayer({ id: newLayerId(), ...common, blendMode, pixelId: surface.id, offset })
    if (mask) layer.mask = mask
    if (fx.styles) layer.styles = fx.styles
    doc.layers[layer.id] = layer
    return layer.id
  }

  for (const child of psd.children ?? []) {
    const id = convert(child)
    if (id) doc.rootChildren.push(id)
  }
  if (!psd.children?.length && psd.imageData) {
    // レイヤー情報の無い PSD: 合成画像を 1 枚のレイヤーに
    const s = surfaceFromPixelData(psd.imageData)
    surfaces.push(s)
    const layer = createRasterLayer({ id: newLayerId(), name: '背景', pixelId: s.id })
    doc.layers[layer.id] = layer
    doc.rootChildren.push(layer.id)
  }
  const gg = psd.imageResources?.gridAndGuidesInformation
  if (gg?.guides) {
    for (const g of gg.guides) doc.guides.push({ id: 'guide_' + newLayerId().slice(3), axis: g.direction === 'vertical' ? 'x' : 'y', position: g.location })
  }
  if (psd.imageResources?.resolutionInfo) {
    const r = psd.imageResources.resolutionInfo
    doc.meta.dpi = Math.round(r.horizontalResolutionUnit === 'PPCM' ? r.horizontalResolution * 2.54 : r.horizontalResolution) || 72
  }
  const last = doc.rootChildren[doc.rootChildren.length - 1]
  doc.activeLayerIds = last ? [last] : []
  return { document: doc, surfaces, report }
}

export interface SurfaceReader {
  /** 表面の内容矩形と RGBA (4ch) または R (1ch)。空なら null */
  read(pixelId: string): { rect: Rect; data: Uint8ClampedArray; channels: 1 | 4 } | null
  /** テキストレイヤーのラスタ (文書座標矩形と RGBA)。無ければ null */
  readText?(layerId: string): { rect: Rect; data: Uint8ClampedArray } | null
}

function hexToRgbLocal(hex: string): { r: number; g: number; b: number } {
  const n = parseInt(hex.slice(1), 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

/** Document を ag-psd の Psd へ。composite は合成画像 (省略可) */
export function documentToPsd(doc: Document, reader: SurfaceReader, composite?: { width: number; height: number; rgba: Uint8ClampedArray }): { psd: Psd; report: ConversionReport } {
  const report: ConversionReport = { warnings: [] }
  const convert = (id: LayerId): PsdLayer | null => {
    const l: Layer | undefined = doc.layers[id]
    if (!l) return null
    const out: PsdLayer = {
      name: l.name,
      hidden: !l.visible,
      opacity: l.opacity,
      fillOpacity: l.fillOpacity,
      blendMode: blendModeToPsd(l.blendMode),
      clipping: l.clipToBelow,
      transparencyProtected: l.lock.transparency || l.lock.all,
      protected: { composite: l.lock.pixels || l.lock.all, position: l.lock.position || l.lock.all, transparency: l.lock.transparency || l.lock.all }
    }
    if (l.mask) {
      const m = reader.read(l.mask.pixelId)
      if (m) {
        const rgba = new Uint8ClampedArray(m.rect.w * m.rect.h * 4)
        for (let i = 0; i < m.rect.w * m.rect.h; i++) {
          const v = m.channels === 1 ? m.data[i]! : m.data[i * 4]!
          rgba[i * 4] = v; rgba[i * 4 + 1] = v; rgba[i * 4 + 2] = v; rgba[i * 4 + 3] = 255
        }
        out.mask = {
          left: m.rect.x + l.mask.offset.x,
          top: m.rect.y + l.mask.offset.y,
          right: m.rect.x + l.mask.offset.x + m.rect.w,
          bottom: m.rect.y + l.mask.offset.y + m.rect.h,
          disabled: !l.mask.enabled,
          defaultColor: 0,
          userMaskDensity: Math.round(l.mask.density * 255),
          userMaskFeather: l.mask.feather,
          imageData: { width: m.rect.w, height: m.rect.h, data: rgba }
        }
      }
    }
    const fxOut = stylesToPsd(l.styles)
    if (fxOut) out.effects = fxOut
    if (l.styles?.patternOverlay?.enabled) report.warnings.push({ layerName: l.name, kind: 'effects', message: 'パターンオーバーレイは PSD に書き出せません' })
    if (l.kind === 'group') {
      out.opened = l.expanded
      out.children = l.children.map(convert).filter((c): c is PsdLayer => !!c)
      return out
    }
    if (l.kind === 'text') {
      // ラスタ画像 + テキスト情報 (Photoshop 側で再描画される)
      const r = reader.readText?.(l.id) ?? null
      if (r) {
        out.left = r.rect.x; out.top = r.rect.y; out.right = r.rect.x + r.rect.w; out.bottom = r.rect.y + r.rect.h
        out.imageData = { width: r.rect.w, height: r.rect.h, data: r.data }
      } else {
        out.left = out.top = out.right = out.bottom = 0
        out.imageData = { width: 0, height: 0, data: new Uint8ClampedArray(0) }
      }
      if (l.warp) report.warnings.push({ layerName: l.name, kind: 'text', message: 'ワープテキストは PSD に書き出せません (ラスタ画像は変形後)' })
      const c = hexToRgbLocal(l.style.hex)
      out.text = {
        text: l.text,
        orientation: l.style.orientation,
        transform: [1, 0, 0, 1, l.position.x, l.position.y],
        style: { font: { name: l.style.fontFamily }, fontSize: l.style.fontSize, fillColor: { r: c.r, g: c.g, b: c.b }, fauxBold: l.style.fontWeight >= 600, fauxItalic: l.style.italic, tracking: Math.round((l.style.letterSpacing / l.style.fontSize) * 1000) },
        paragraphStyle: { justification: l.style.align === 'center' ? 'center' : l.style.align === 'right' ? 'right' : 'left' }
      }
      return out
    }
    if (l.kind === 'shape') {
      const r = reader.readText?.(l.id) ?? null
      if (r) {
        out.left = r.rect.x; out.top = r.rect.y; out.right = r.rect.x + r.rect.w; out.bottom = r.rect.y + r.rect.h
        out.imageData = { width: r.rect.w, height: r.rect.h, data: r.data }
      } else {
        out.left = out.top = out.right = out.bottom = 0
        out.imageData = { width: 0, height: 0, data: new Uint8ClampedArray(0) }
      }
      report.warnings.push({ layerName: l.name, kind: 'vector', message: 'シェイプはラスタ画像として書き出します' })
      return out
    }
    if (l.kind === 'smart') {
      const r = reader.readText?.(l.id) ?? null
      if (r) {
        out.left = r.rect.x; out.top = r.rect.y; out.right = r.rect.x + r.rect.w; out.bottom = r.rect.y + r.rect.h
        out.imageData = { width: r.rect.w, height: r.rect.h, data: r.data }
      } else {
        out.left = out.top = out.right = out.bottom = 0
        out.imageData = { width: 0, height: 0, data: new Uint8ClampedArray(0) }
      }
      report.warnings.push({ layerName: l.name, kind: 'smart', message: 'スマートオブジェクトは変形結果のラスタ画像として書き出します' })
      return out
    }
    if (l.kind === 'adjustment') {
      const adj = adjustmentToPsd(l.adjustment)
      if (!adj) {
        report.warnings.push({ layerName: l.name, kind: 'adjustment', message: `調整レイヤー (${l.adjustment.kind}) は PSD に書き出せません` })
        return null
      }
      if (l.adjustment.kind === 'hue-saturation' && l.adjustment.colorize) report.warnings.push({ layerName: l.name, kind: 'adjustment', message: '色相・彩度の「色彩の統一」は PSD に書き出せません' })
      out.adjustment = adj
      out.left = out.top = out.right = out.bottom = 0
      out.imageData = { width: 0, height: 0, data: new Uint8ClampedArray(0) }
      return out
    }
    if (l.kind === 'fill') {
      if (l.fill.kind === 'pattern') report.warnings.push({ layerName: l.name, kind: 'other', message: 'パターン塗りつぶしは PSD に書き出せません (グレーのべた塗りになります)' })
      out.vectorFill = fillToPsd(l.fill)
      out.left = out.top = out.right = out.bottom = 0
      out.imageData = { width: 0, height: 0, data: new Uint8ClampedArray(0) }
      return out
    }
    const s = reader.read(l.pixelId)
    if (s) {
      out.left = s.rect.x + l.offset.x
      out.top = s.rect.y + l.offset.y
      out.right = out.left + s.rect.w
      out.bottom = out.top + s.rect.h
      out.imageData = { width: s.rect.w, height: s.rect.h, data: s.data }
    } else {
      out.left = out.top = out.right = out.bottom = 0
      out.imageData = { width: 0, height: 0, data: new Uint8ClampedArray(0) }
    }
    return out
  }
  const psd: Psd = {
    width: doc.meta.width,
    height: doc.meta.height,
    channels: 4,
    bitsPerChannel: 8,
    colorMode: 3,
    children: doc.rootChildren.map(convert).filter((c): c is PsdLayer => !!c)
  }
  if (composite) psd.imageData = { width: composite.width, height: composite.height, data: composite.rgba }
  psd.imageResources = {
    resolutionInfo: { horizontalResolution: doc.meta.dpi, horizontalResolutionUnit: 'PPI', widthUnit: 'Inches', verticalResolution: doc.meta.dpi, verticalResolutionUnit: 'PPI', heightUnit: 'Inches' },
    ...(doc.guides.length ? { gridAndGuidesInformation: { grid: { horizontal: 576, vertical: 576 }, guides: doc.guides.map(g => ({ location: g.position, direction: g.axis === 'x' ? 'vertical' as const : 'horizontal' as const })) } } : {})
  }
  if (doc.channels.length) report.warnings.push({ layerName: '', kind: 'other', message: `保存した選択範囲 (${doc.channels.length} 件) は PSD に書き出しません` })
  return { psd, report }
}
